"use client";

import { Toast } from "@base-ui/react/toast";
import {
  CircleAlertIcon,
  CircleCheckIcon,
  InfoIcon,
  LoaderCircleIcon,
  TriangleAlertIcon,
  XIcon,
} from "lucide-react";
import type React from "react";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

const TOAST_ICONS = {
  error: CircleAlertIcon,
  info: InfoIcon,
  loading: LoaderCircleIcon,
  success: CircleCheckIcon,
  warning: TriangleAlertIcon,
} as const;

type WorkflowToastStepStatus = "blocked" | "completed" | "running";

export type WorkflowToastStep = {
  description: string;
  id: string;
  meta?: string;
  status: WorkflowToastStepStatus;
  title: string;
};

export type WorkflowToastPreview = {
  description: string;
  eyebrow: string;
  footer?: string;
  steps: readonly WorkflowToastStep[];
  title: string;
};

type WorkflowToastData = {
  tooltipStyle?: boolean;
  workflowPreview?: WorkflowToastPreview;
};

type SwipeDirection = "up" | "down" | "left" | "right";

function getWorkflowToastPreview(data: unknown): WorkflowToastPreview | null {
  return (data as WorkflowToastData | undefined)?.workflowPreview ?? null;
}

function WorkflowStepStatusIcon({
  status,
}: {
  status: WorkflowToastStepStatus;
}): React.ReactElement {
  if (status === "running") {
    return (
      <LoaderCircleIcon
        aria-hidden="true"
        className="mt-0.5 size-4 shrink-0 animate-spin text-[oklch(0.76_0.14_82)]"
      />
    );
  }

  if (status === "blocked") {
    return (
      <TriangleAlertIcon
        aria-hidden="true"
        className="mt-0.5 size-4 shrink-0 text-[oklch(0.74_0.17_45)]"
      />
    );
  }

  return (
    <CircleCheckIcon
      aria-hidden="true"
      className="mt-0.5 size-4 shrink-0 text-[oklch(0.70_0.17_155)]"
    />
  );
}

function WorkflowToastContent({
  onClose,
  preview,
}: {
  onClose: () => void;
  preview: WorkflowToastPreview;
}): React.ReactElement {
  return (
    <Toast.Content className="pointer-events-auto overflow-hidden p-0 text-sm transition-opacity duration-250 data-behind:not-data-expanded:pointer-events-none data-behind:opacity-0 data-expanded:opacity-100">
      <div className="min-w-0 bg-[oklch(0.145_0.008_260)] text-[oklch(0.94_0.004_260)]">
        <div className="flex items-start justify-between gap-4 border-b border-[oklch(1_0_0/0.09)] px-4 py-3">
          <div className="min-w-0">
            <div className="text-xs leading-4 text-[oklch(0.66_0.018_260)]">
              {preview.eyebrow}
            </div>
            <Toast.Title
              className="mt-1 break-words text-[15px] leading-5 text-[oklch(0.97_0.004_260)]"
              data-slot="toast-title"
            >
              {preview.title}
            </Toast.Title>
            <Toast.Description
              className="mt-1 break-words text-xs leading-5 text-[oklch(0.66_0.018_260)]"
              data-slot="toast-description"
            >
              {preview.description}
            </Toast.Description>
          </div>
          <button
            aria-label="Dismiss notification"
            className="-mr-1 inline-flex size-7 shrink-0 items-center justify-center rounded-md text-[oklch(0.66_0.018_260)] outline-none transition-colors hover:bg-[oklch(1_0_0/0.08)] hover:text-[oklch(0.96_0.004_260)] focus-visible:ring-2 focus-visible:ring-[oklch(0.68_0.16_263)]"
            onClick={onClose}
            type="button"
          >
            <XIcon aria-hidden="true" className="size-4" />
          </button>
        </div>

        <div className="space-y-1.5 p-3">
          {preview.steps.map((step) => (
            <div
              className="grid grid-cols-[1rem_minmax(0,1fr)] gap-x-3 gap-y-2 rounded-md border border-[oklch(1_0_0/0.08)] bg-[oklch(1_0_0/0.035)] px-3 py-2.5 sm:grid-cols-[1rem_minmax(0,1fr)_auto]"
              key={step.id}
            >
              <WorkflowStepStatusIcon status={step.status} />
              <div className="min-w-0">
                <div className="break-words text-sm leading-5 text-[oklch(0.93_0.004_260)]">
                  {step.title}
                </div>
                <div className="mt-0.5 break-words text-xs leading-4 text-[oklch(0.61_0.018_260)]">
                  {step.description}
                </div>
                <span className="sr-only">{step.status}</span>
              </div>
              {step.meta && (
                <div className="col-start-2 w-fit max-w-full rounded border border-[oklch(1_0_0/0.07)] bg-[oklch(0.10_0.006_260)] px-2 py-1 text-xs leading-4 text-[oklch(0.74_0.02_260)] sm:col-start-auto sm:max-w-44 sm:text-right">
                  {step.meta}
                </div>
              )}
            </div>
          ))}
        </div>

        {preview.footer && (
          <div className="border-t border-[oklch(1_0_0/0.08)] px-4 py-3 text-xs leading-5 text-[oklch(0.64_0.018_260)]">
            {preview.footer}
          </div>
        )}
      </div>
    </Toast.Content>
  );
}

function getSwipeDirection(position: ToastPosition): SwipeDirection[] {
  const verticalDirection: SwipeDirection = position.startsWith("top")
    ? "up"
    : "down";

  if (position.includes("center")) {
    return [verticalDirection];
  }

  if (position.includes("left")) {
    return ["left", verticalDirection];
  }

  return ["right", verticalDirection];
}

function Toasts({ position }: { position: ToastPosition }): React.ReactElement {
  const { toasts } = Toast.useToastManager();
  const swipeDirection = getSwipeDirection(position);
  const hasWorkflowPreviewToast = toasts.some((toast) =>
    Boolean(getWorkflowToastPreview(toast.data)),
  );

  return (
    <Toast.Portal data-slot="toast-portal">
      <Toast.Viewport
        className={cn(
          "fixed left-(--toast-inset) z-60 flex w-[calc(100vw-var(--toast-inset)*2)] [--toast-inset:--spacing(4)] sm:[--toast-inset:--spacing(8)]",
          hasWorkflowPreviewToast
            ? "max-w-[min(42rem,calc(100vw-var(--toast-inset)*2))]"
            : "max-w-90",
          // Vertical positioning
          "data-[position*=top]:top-(--toast-inset)",
          "data-[position*=bottom]:top-[calc(100vh-var(--toast-inset))]",
          // Horizontal positioning
          "sm:data-[position*=right]:right-(--toast-inset) sm:data-[position*=right]:left-auto",
          "data-[position*=center]:left-1/2 data-[position*=center]:-translate-x-1/2",
        )}
        data-position={position}
        data-slot="toast-viewport"
      >
        {toasts.map((toast) => {
          const Icon = toast.type
            ? TOAST_ICONS[toast.type as keyof typeof TOAST_ICONS]
            : null;
          const workflowPreview = getWorkflowToastPreview(toast.data);

          return (
            <Toast.Root
              className={cn(
                "dark absolute z-[calc(9999-var(--toast-index))] h-(--toast-calc-height) w-full select-none rounded-lg border border-border bg-popover text-popover-foreground shadow-[0_18px_48px_-18px_rgb(0_0_0/0.56),0_8px_18px_-10px_rgb(0_0_0/0.36)] outline-none [transition:transform_.5s_cubic-bezier(.22,1,.36,1),opacity_.5s,height_.15s,background-color_.5s] data-expanded:bg-popover",
                // Base positioning using data-position
                "data-[position*=right]:right-0 data-[position*=right]:left-auto",
                "data-[position*=left]:right-auto data-[position*=left]:left-0",
                "data-[position*=center]:right-0 data-[position*=center]:left-0",
                "data-[position*=top]:top-0 data-[position*=top]:bottom-auto data-[position*=top]:origin-top",
                "data-[position*=bottom]:top-auto data-[position*=bottom]:bottom-0 data-[position*=bottom]:origin-bottom",
                // Gap fill for hover
                "after:absolute after:left-0 after:h-[calc(var(--toast-gap)+1px)] after:w-full",
                "data-[position*=top]:after:top-full",
                "data-[position*=bottom]:after:bottom-full",
                // Define some variables
                "[--toast-calc-height:var(--toast-frontmost-height,var(--toast-height))] [--toast-gap:--spacing(3)] [--toast-peek:--spacing(3)] [--toast-scale:calc(max(0,1-(var(--toast-index)*.1)))] [--toast-shrink:calc(1-var(--toast-scale))]",
                // Define offset-y variable
                "data-[position*=top]:[--toast-calc-offset-y:calc(var(--toast-offset-y)+var(--toast-index)*var(--toast-gap)+var(--toast-swipe-movement-y))]",
                "data-[position*=bottom]:[--toast-calc-offset-y:calc(var(--toast-offset-y)*-1+var(--toast-index)*var(--toast-gap)*-1+var(--toast-swipe-movement-y))]",
                // Default state transform
                "data-[position*=top]:transform-[translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)+(var(--toast-index)*var(--toast-peek))+(var(--toast-shrink)*var(--toast-calc-height))))_scale(var(--toast-scale))]",
                "data-[position*=bottom]:transform-[translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)-(var(--toast-index)*var(--toast-peek))-(var(--toast-shrink)*var(--toast-calc-height))))_scale(var(--toast-scale))]",
                // Limited state
                "data-limited:opacity-0",
                // Expanded state
                "data-expanded:h-(--toast-height)",
                "data-position:data-expanded:transform-[translateX(var(--toast-swipe-movement-x))_translateY(var(--toast-calc-offset-y))]",
                // Starting and ending animations
                "data-[position*=top]:data-starting-style:transform-[translateY(calc(-100%-var(--toast-inset)))]",
                "data-[position*=bottom]:data-starting-style:transform-[translateY(calc(100%+var(--toast-inset)))]",
                "data-ending-style:opacity-0",
                // Ending animations (direction-aware)
                "data-ending-style:not-data-limited:not-data-swipe-direction:transform-[translateY(calc(100%+var(--toast-inset)))]",
                "data-ending-style:data-[swipe-direction=left]:transform-[translateX(calc(var(--toast-swipe-movement-x)-100%-var(--toast-inset)))_translateY(var(--toast-calc-offset-y))]",
                "data-ending-style:data-[swipe-direction=right]:transform-[translateX(calc(var(--toast-swipe-movement-x)+100%+var(--toast-inset)))_translateY(var(--toast-calc-offset-y))]",
                "data-ending-style:data-[swipe-direction=up]:transform-[translateY(calc(var(--toast-swipe-movement-y)-100%-var(--toast-inset)))]",
                "data-ending-style:data-[swipe-direction=down]:transform-[translateY(calc(var(--toast-swipe-movement-y)+100%+var(--toast-inset)))]",
                // Ending animations (expanded)
                "data-expanded:data-ending-style:data-[swipe-direction=left]:transform-[translateX(calc(var(--toast-swipe-movement-x)-100%-var(--toast-inset)))_translateY(var(--toast-calc-offset-y))]",
                "data-expanded:data-ending-style:data-[swipe-direction=right]:transform-[translateX(calc(var(--toast-swipe-movement-x)+100%+var(--toast-inset)))_translateY(var(--toast-calc-offset-y))]",
                "data-expanded:data-ending-style:data-[swipe-direction=up]:transform-[translateY(calc(var(--toast-swipe-movement-y)-100%-var(--toast-inset)))]",
                "data-expanded:data-ending-style:data-[swipe-direction=down]:transform-[translateY(calc(var(--toast-swipe-movement-y)+100%+var(--toast-inset)))]",
              )}
              data-position={position}
              key={toast.id}
              swipeDirection={swipeDirection}
              toast={toast}
            >
              {workflowPreview ? (
                <WorkflowToastContent
                  onClose={() => toastManager.close(toast.id)}
                  preview={workflowPreview}
                />
              ) : (
                <Toast.Content className="pointer-events-auto flex items-start justify-between gap-3 overflow-hidden px-4 py-3.5 text-sm transition-opacity duration-250 data-behind:not-data-expanded:pointer-events-none data-behind:opacity-0 data-expanded:opacity-100">
                  <div className="flex min-w-0 gap-2.5">
                    {Icon && (
                      <div
                        className="mt-0.5 flex size-4.5 shrink-0 items-center justify-center [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                        data-slot="toast-icon"
                      >
                        <Icon className="in-data-[type=loading]:animate-spin in-data-[type=error]:text-destructive in-data-[type=info]:text-info in-data-[type=success]:text-success in-data-[type=warning]:text-warning in-data-[type=loading]:opacity-80" />
                      </div>
                    )}

                    <div className="flex min-w-0 flex-col gap-0.5">
                      <Toast.Title
                        className="break-words text-foreground leading-5"
                        data-slot="toast-title"
                      />
                      <Toast.Description
                        className="break-words text-muted-foreground leading-5"
                        data-slot="toast-description"
                      />
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {toast.actionProps && (
                      <Toast.Action
                        {...toast.actionProps}
                        className={buttonVariants({
                          size: "sm",
                          variant: "outline",
                        })}
                        data-slot="toast-action"
                      />
                    )}
                    <button
                      aria-label="Dismiss notification"
                      className="-mr-1 mt-0.5 inline-flex size-6 items-center justify-center rounded-md text-muted-foreground outline-none transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                      onClick={() => toastManager.close(toast.id)}
                      type="button"
                    >
                      <XIcon aria-hidden="true" className="size-4" />
                    </button>
                  </div>
                </Toast.Content>
              )}
            </Toast.Root>
          );
        })}
      </Toast.Viewport>
    </Toast.Portal>
  );
}

function AnchoredToasts(): React.ReactElement {
  const { toasts } = Toast.useToastManager();

  return (
    <Toast.Portal data-slot="toast-portal-anchored">
      <Toast.Viewport
        className="outline-none"
        data-slot="toast-viewport-anchored"
      >
        {toasts.map((toast) => {
          const Icon = toast.type
            ? TOAST_ICONS[toast.type as keyof typeof TOAST_ICONS]
            : null;
          const tooltipStyle =
            (toast.data as WorkflowToastData | undefined)?.tooltipStyle ?? false;
          const workflowPreview = getWorkflowToastPreview(toast.data);
          const positionerProps = toast.positionerProps;

          if (!positionerProps?.anchor) {
            return null;
          }

          return (
            <Toast.Positioner
              className={cn(
                "z-50",
                workflowPreview
                  ? "max-w-[min(42rem,var(--available-width))]"
                  : "max-w-[min(--spacing(64),var(--available-width))]",
              )}
              data-slot="toast-positioner"
              key={toast.id}
              sideOffset={positionerProps.sideOffset ?? 4}
              toast={toast}
            >
              <Toast.Root
                className={cn(
                  "relative border border-[oklch(1_0_0/0.10)] bg-[oklch(0.18_0_0)] text-[oklch(0.93_0_0)] text-xs transition-[scale,opacity] data-ending-style:scale-98 data-starting-style:scale-98 data-ending-style:opacity-0 data-starting-style:opacity-0",
                  workflowPreview
                    ? "w-[min(42rem,calc(100vw-2rem))] overflow-hidden rounded-lg shadow-[0_24px_64px_-18px_rgb(0_0_0/0.6),0_8px_20px_-10px_rgb(0_0_0/0.36)]"
                    : "text-balance",
                  tooltipStyle && !workflowPreview
                    ? "rounded-md shadow-[0_16px_48px_-12px_rgb(0_0_0/0.5),0_4px_14px_-2px_rgb(0_0_0/0.28)]"
                    : "rounded-lg shadow-[0_16px_48px_-12px_rgb(0_0_0/0.5),0_4px_14px_-2px_rgb(0_0_0/0.28)]",
                )}
                data-slot="toast-popup"
                toast={toast}
              >
                {workflowPreview ? (
                  <WorkflowToastContent
                    onClose={() => anchoredToastManager.close(toast.id)}
                    preview={workflowPreview}
                  />
                ) : tooltipStyle ? (
                  <Toast.Content className="pointer-events-auto px-2 py-1">
                    <Toast.Title data-slot="toast-title" />
                  </Toast.Content>
                ) : (
                  <Toast.Content className="pointer-events-auto flex items-center justify-between gap-1.5 overflow-hidden px-3.5 py-3 text-sm">
                    <div className="flex gap-2">
                      {Icon && (
                        <div
                          className="[&>svg]:h-lh [&>svg]:w-4 [&_svg]:pointer-events-none [&_svg]:shrink-0"
                          data-slot="toast-icon"
                        >
                          <Icon className="in-data-[type=loading]:animate-spin in-data-[type=error]:text-destructive in-data-[type=info]:text-info in-data-[type=success]:text-success in-data-[type=warning]:text-warning in-data-[type=loading]:opacity-80" />
                        </div>
                      )}

                      <div className="flex flex-col gap-0.5">
                        <Toast.Title
                          className="font-normal"
                          data-slot="toast-title"
                        />
                        <Toast.Description
                          className="text-[oklch(0.55_0_0)]"
                          data-slot="toast-description"
                        />
                      </div>
                    </div>
                    {toast.actionProps && (
                      <Toast.Action
                        {...toast.actionProps}
                        className={buttonVariants({ size: "sm" })}
                        data-slot="toast-action"
                      />
                    )}
                  </Toast.Content>
                )}
              </Toast.Root>
            </Toast.Positioner>
          );
        })}
      </Toast.Viewport>
    </Toast.Portal>
  );
}

export const toastManager: ReturnType<typeof Toast.createToastManager> =
  Toast.createToastManager();
export const anchoredToastManager: ReturnType<typeof Toast.createToastManager> =
  Toast.createToastManager();

export type ToastPosition =
  | "top-left"
  | "top-center"
  | "top-right"
  | "bottom-left"
  | "bottom-center"
  | "bottom-right";

export interface ToastProviderProps extends Toast.Provider.Props {
  position?: ToastPosition;
}

export function ToastProvider({
  children,
  position = "bottom-right",
  ...props
}: ToastProviderProps): React.ReactElement {
  return (
    <Toast.Provider toastManager={toastManager} {...props}>
      {children}
      <Toasts position={position} />
    </Toast.Provider>
  );
}

export function AnchoredToastProvider({
  children,
  ...props
}: Toast.Provider.Props): React.ReactElement {
  return (
    <Toast.Provider toastManager={anchoredToastManager} {...props}>
      {children}
      <AnchoredToasts />
    </Toast.Provider>
  );
}

export { Toast as ToastPrimitive };
